import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import Card from './Common/Card';
import SectionTitle from './Common/SectionTitle';
import { supabase } from '../lib/supabase';

// Custom hook for in-view detection
const useInView = (ref, threshold = 0.1) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!ref.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { threshold }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref, threshold]);

  return isVisible;
};

// Loading skeleton component
const NewsSkeleton = () => (
  <Card className="p-6">
    <div className="flex flex-col md:flex-row gap-4">
      <div className="md:w-48 w-full aspect-[4/3] bg-slate-200 rounded-lg flex-shrink-0" />
      <div className="flex-1 space-y-2 p-2">
        <div className="h-4 bg-slate-200 rounded w-1/4" />
        <div className="h-3 bg-slate-200 rounded w-1/3" />
        <div className="h-3 bg-slate-200 rounded w-1/4" />
        <div className="h-6 bg-slate-200 rounded w-3/4 mt-2" />
        <div className="h-3 bg-slate-200 rounded w-full" />
        <div className="h-3 bg-slate-200 rounded w-5/6" />
      </div>
    </div>
  </Card>
);

const NewsEvents = () => {
  const sectionRef = useRef(null);
  const isVisible = useInView(sectionRef);

  const [newsItems, setNewsItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedItems, setExpandedItems] = useState({});
  const [selectedImage, setSelectedImage] = useState(null);

  // Fetch news items
  const fetchNews = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('news_events')
        .select('*')
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .limit(5);

      if (error) {
        console.error('Error fetching news:', error);
        setNewsItems([]);
      } else {
        setNewsItems(data || []);
      }
    } catch (err) {
      console.error('Error fetching news:', err);
      setNewsItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNews();
  }, []);

  const toggleExpanded = (itemId) => {
    setExpandedItems((prev) => ({ ...prev, [itemId]: !prev[itemId] }));
  };

  const openImageModal = (imageUrl, title) => setSelectedImage({ url: imageUrl, title });
  const closeImageModal = () => setSelectedImage(null);

  const truncateText = (text, maxLength = 150) =>
    text.length <= maxLength ? text : text.substring(0, maxLength) + '...';

  const formatEventDate = (start, end) => {
    if (!start) return '-';
    const startDate = new Date(start);
    const endDate = end ? new Date(end) : null;
    const options = { day: '2-digit', month: 'short', year: 'numeric' };

    if (endDate && endDate.getTime() !== startDate.getTime()) {
      return `${startDate.toLocaleDateString('en-GB', options)} – ${endDate.toLocaleDateString('en-GB', options)}`;
    }
    return startDate.toLocaleDateString('en-GB', options);
  };

  const getBadgeColors = (type) => {
    switch (type) {
      case 'news':
        return 'bg-blue-100 text-blue-800';
      case 'events':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-purple-100 text-purple-800';
    }
  };

  return (
    <section ref={sectionRef} className="mb-16">
      <div
        className={`transition-all duration-1000 ease-out transform ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}
      >
        <SectionTitle>News & Announcements</SectionTitle>
      </div>

      <div className="space-y-4 mt-4">
        {loading
          ? Array.from({ length: 3 }).map((_, i) => <NewsSkeleton key={i} />)
          : newsItems.length > 0
          ? newsItems.map((item, index) => {
              const isExpanded = expandedItems[item.id];
              const displayContent = isExpanded ? item.content : truncateText(item.content);
              const shouldTruncate = item.content.length > 150;

              return (
                <motion.div
                  key={item.id}
                  className={`transition-all duration-700 ease-out transform ${
                    isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
                  }`}
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  <Card className="group overflow-hidden hover:shadow-xl hover:scale-[1.02] transition-all duration-300 p-0">
                    <div className="flex flex-col md:flex-row">
                      {/* Image */}
                      <div className="md:w-48 w-full aspect-[4/3] flex-shrink-0">
                        <div
                          role="img"
                          aria-label={item.title}
                          className="w-full h-full bg-center bg-cover cursor-pointer transition-transform duration-300 hover:scale-105"
                          style={{ backgroundImage: `url("${item.image_url || '/images/placeholder-news.jpg'}")` }}
                          onClick={() => openImageModal(item.image_url, item.title)}
                        />
                      </div>

                      {/* Content */}
                      <div className="flex-1 p-4 md:p-6">
                        <div className="flex items-center gap-3 mb-3 flex-wrap">
                          <span className={`px-3 py-1 text-xs font-semibold rounded-full ${getBadgeColors(item.type)}`}>
                            {item.type?.toUpperCase() || 'NEWS'}
                          </span>
                          {item.start_date && (
                            <span className="text-xs text-slate-500 flex items-center gap-1">
                              📅 {formatEventDate(item.start_date, item.end_date)}
                            </span>
                          )}
                          {item.location && (
                            <span className="text-xs text-slate-500 flex items-center gap-1">
                              📍 {item.location}
                            </span>
                          )}
                        </div>

                        <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-primary transition-colors">
                          {item.title}
                        </h3>

                        <motion.div
                          animate={{ height: isExpanded ? 'auto' : 80 }}
                          transition={{ duration: 0.3 }}
                          className="overflow-hidden mb-2"
                        >
                          <p className="text-slate-600 leading-relaxed whitespace-pre-line">{displayContent}</p>
                        </motion.div>

                        {shouldTruncate && (
                          <button
                            onClick={() => toggleExpanded(item.id)}
                            className="text-primary font-medium text-sm cursor-pointer"
                          >
                            {isExpanded ? 'Read Less' : 'Read More'}
                          </button>
                        )}
                      </div>
                    </div>
                  </Card>
                </motion.div>
              );
            })
          : (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">📰</div>
              <h3 className="text-xl font-semibold text-slate-600 mb-2">No News Available</h3>
              <p className="text-slate-500">Check back later for the latest updates.</p>
            </div>
          )}
      </div>

      {/* Join Us Card */}
      <div className="mt-12">
        <Card className="p-6 text-center hover:shadow-xl transition-all duration-300 cursor-pointer">
          <h3 className="text-2xl font-bold text-slate-900 mb-2">Join Us Today!</h3>
          <p className="text-slate-600 mb-4">
            Take the next step in your academic journey and become part of Anestar Victory.
          </p>
          <Link
            to="/admissions"
            className="inline-block bg-royal-blue text-white px-6 py-3 rounded-full font-semibold hover:bg-yellow-500 transition-colors duration-300"
          >
            Admissions
          </Link>
        </Card>
      </div>

      {/* Image Modal */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
            onClick={closeImageModal}
          >
            <div className="relative max-w-4xl max-h-[90vh] mx-4" onClick={(e) => e.stopPropagation()}>
              <button
                onClick={closeImageModal}
                className="absolute -top-12 right-0 text-white hover:text-gray-300"
                aria-label="Close Image Modal"
              >
                ×
              </button>
              <div className="bg-white rounded-lg overflow-hidden shadow-2xl">
                <img
                  src={selectedImage.url || '/images/placeholder-news.jpg'}
                  alt={selectedImage.title}
                  className="max-w-full max-h-[70vh] object-contain"
                />
                <div className="p-4">
                  <h3 className="text-lg font-semibold text-slate-900">{selectedImage.title}</h3>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default NewsEvents;
